import type { DiffSegment } from '@/types/listing';

export interface DiffTextProps {
  segments: DiffSegment[];
  highlight: boolean;
}

/**
 * Renders a segment list produced by `DiffListingContent`
 * (`decisions/listing-diffs-are-segmented-at-pull-time.md`). Segments
 * render adjacently with no inserted spacing between them -- the
 * whitespace already travels with each segment's own `text`, so
 * `same + added` reconstructs this version's string byte for byte and
 * `same + removed` reconstructs its predecessor's. Inserting a space here
 * would visibly corrupt text the backend guarantees is exact.
 *
 * `Highlight changes` **off** is the filter `same + added`, not a
 * re-render of some other string: `removed` segments are dropped
 * entirely rather than un-styled, and `added` loses its background but
 * stays in the flow as plain text
 * (`design-input/listing-content-panel/prototype-extracted.md § Diff
 * rendering`).
 */
export function DiffText({ segments, highlight }: DiffTextProps) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'removed') {
          if (!highlight) return null;
          return (
            <span key={index} className="text-muted-foreground line-through">
              {segment.text}
            </span>
          );
        }
        if (segment.type === 'added' && highlight) {
          return (
            <span key={index} className="bg-status-info-soft rounded-sm px-0.5">
              {segment.text}
            </span>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </>
  );
}

/**
 * The all-`removed` bullet trap
 * (`decisions/listing-diffs-are-segmented-at-pull-time.md`): index
 * alignment means a bullet dropped entirely from the new version arrives
 * as an entry whose every segment is `removed`. With the switch off that
 * entry has nothing left to show, so it must render no marker and no row
 * at all -- not an empty one.
 */
export const bulletHasVisibleContent = (segments: DiffSegment[], highlight: boolean): boolean =>
  highlight || segments.some((segment) => segment.type !== 'removed');

/**
 * `FR-23`: true when the selected version's title or any bullet carries a
 * segment that isn't `same` -- mirrors the prototype's own `hasChanges`
 * (`design-input/listing-content-panel/prototype-extracted.md § Diff
 * rendering`). `No changes in this version` is shown exactly when this is
 * `false`.
 */
export const hasDiffChanges = (title: DiffSegment[], bullets: DiffSegment[][]): boolean =>
  [title, ...bullets].flat().some((segment) => segment.type !== 'same');
